import { MenuItem as BackendMenuItem } from '../store/type';
import {
  MenuItem as SiderMenuItem,
  userList,
} from '../pages/layout/components/sider/menuList';
import {
  IconAppCenter,
  IconForward,
  IconUpload,
  IconStar,
  IconCopyAdd,
  IconEmoji,
  IconMember,
  IconLikeHeart,
  IconBox,
  IconSend,
  IconUserGroup,
  IconClock,
  IconFlag,
  IconSearch,
  IconSetting,
  IconShoppingBag,
  IconCart,
  IconGlobe,
  IconHome,
} from '@douyinfe/semi-icons';
import useUserStore from '@/store/user';
import {
  menuList as staticMenuList,
  adminList,
} from '../pages/layout/components/sider/menuList';
import { isAdmin } from './auth';

// Function to map path to icon
function getIconByPath(path: string | undefined): React.ReactNode | undefined {
  if (!path) return undefined;
  switch (path) {
    case 'listing':
      return <IconUpload />; // 亚马逊上架
    case 'collect':
      return <IconForward />; // 链接上架
    case 'follow':
      return <IconCopyAdd />; // NA跟卖
    case 'advert':
      return <IconEmoji />; // 广告列表
    case 'advert-plus':
      return <IconEmoji />; // 广告管理+
    case 'sales':
      return <IconMember />; // 销量统计
    case 'cate':
      return <IconStar />; // 类目采集
    case 'bestSell':
      return <IconStar />; // Best Sell
    case 'alibaba':
      return <IconLikeHeart />; // 1688采集
    case 'procure':
      return <IconBox />; // 采购管理
    case 'invoice':
      return <IconSend />; // 发货管理
    case 'invoice-plus':
      return <IconSend />; // 发货管理+
    case 'subAccount':
      return <IconUserGroup />; // 子账号管理
    case 'asn':
      return <IconClock />; // 自动约仓
    case 'replenish-plan':
      return <IconBox />; // 补货计划
    case 'deal':
      return <IconFlag />; // 活动管理
    case 'sku-keyword':
      return <IconSearch />; // SKU关键词
    case 'admin/customer-feature':
      return <IconSetting />; // 功能开关（老 uri 兼容）
    case 'customer-feature':
      return <IconSetting />; // 功能开关
    case 'store-authorization':
      return <IconShoppingBag />; // 店铺授权
    case 'user/cate':
      return <IconStar />; // 类目采集
    case 'admin/noon_order/list':
      return <IconCart />; // 测评列表
    case 'fbp-duration':
      return <IconClock />; // FBP时长
    case 'asn-grabber':
      return <IconClock />; // 抢仓插件
    case 'extension':
      return <IconGlobe />; // 扩展指南
    case 'warehouse':
      return <IconHome />; // 仓库管理
    case 'finance':
      return <IconBox />; // 财务报表
    default:
      return <IconAppCenter />; // Default icon
  }
}

// Helper function to recursively convert child menus
function convertChildMenu(childMenus: BackendMenuItem[]): SiderMenuItem[] {
  return childMenus.map((item) => ({
    itemKey: String(item.id),
    text: `web.menu.${item.uri.replace(/\//g, '.')}`,
    path: `/${item.uri}`,
    icon: getIconByPath(item.uri),
    items:
      item.child && item.child.length > 0
        ? convertChildMenu(item.child)
        : undefined,
  }));
}

// 前端固定菜单排序（与后端返回顺序无关）；前 5 项为用户指定；其后按常用频率递减排列
const MENU_ORDER: string[] = [
  '/dashboard',            // 1 我的主页
  '/follow',               // 2 Catalog
  '/user/cate',            // 3 选品助手
  '/bestSell',             // 4 Best Sell
  '/collect',              // 5 链接上架
  '/advert',               // 广告列表（常用）
  '/advert-plus',          // 广告管理+
  '/sales',                // 销量统计
  '/finance',              // 财务报表
  '/procure',              // 采购管理
  '/replenish-plan',       // 补货计划
  '/invoice',              // 发货管理
  '/invoice-plus',         // 发货管理+
  '/listing',              // 亚马逊上架
  '/alibaba',              // 1688 上架
  '/deal',                 // 活动管理
  '/warehouse',            // 仓库管理
  '/asn',                  // 自动约仓
  '/sku-keyword',          // SKU关键词
  '/fbp-duration',         // FBP时长
  '/customer-feature',     // 功能开关
  '/subAccount',           // 子账号管理
  '/admin/noon_order/list',// 测评列表
  '/store-authorization',  // 店铺授权
  '/asn-grabber',          // 抢仓插件
  '/extension',            // 扩展指南
  '/admin/buyer/list',     // 买家列表（admin）
  '/admin/account',        // 账号管理（admin）
  '/admin/menus',          // 权限管理（admin）
];

function menuOrderIndex(path: string | undefined): number {
  if (!path) return Number.MAX_SAFE_INTEGER;
  const idx = MENU_ORDER.indexOf(path);
  return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
}

export function convertBackendMenuToSiderMenu(): SiderMenuItem[] {
  const backendMenus = useUserStore.getState().menuList;

  if (!backendMenus || backendMenus.length === 0) {
    return staticMenuList; // 如果后端菜单为空，则返回静态菜单
  }

  // 收集所有前端静态菜单的路径，用于自动去重后端返回的重复菜单
  const roleMenus = isAdmin(useUserStore.getState().userInfo)
    ? adminList
    : userList;
  const staticPaths = new Set<string>();
  function collectPaths(items: SiderMenuItem[]) {
    for (const item of items) {
      if (item.path) staticPaths.add(item.path);
      if (item.items) collectPaths(item.items);
    }
  }
  collectPaths(staticMenuList);
  collectPaths(roleMenus);

  const filteredBackendMenus = backendMenus.filter(
    (item) => item.uri !== 'goods' && !staticPaths.has(`/${item.uri}`)
  );

  const convertedBackendMenus = filteredBackendMenus.map((item) => ({
    itemKey: String(item.id),
    text: `web.menu.${item.uri.replace(/\//g, '.')}`,
    path: `/${item.uri}`,
    icon: getIconByPath(item.uri),
    items:
      item.child && item.child.length > 0
        ? convertChildMenu(item.child)
        : undefined,
  }));

  const finalMenus = [
    ...staticMenuList,
    ...convertedBackendMenus,
    ...roleMenus,
  ];

  // 前端固定排序，忽略后端顺序。未在 MENU_ORDER 声明的 path 会排到末尾并按声明出现顺序保持稳定
  finalMenus.sort((a, b) => {
    const ai = menuOrderIndex(a.path);
    const bi = menuOrderIndex(b.path);
    if (ai !== bi) return ai - bi;
    return 0;
  });

  return finalMenus;
}
